import { useFetchPage } from "~/core/composables/useFetchPage";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_EQUAL,
  OPERATOR_RSQL_ILIKE,
  OPERATOR_RSQL_IN,
} from "~/core/models/filters";
import OperationManagerService from "~/core/services/operationManager.service";
import filtersUtils from "~/core/utils/filters.utils";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";

export function useFetchOperations({
  searchMode,
  useQueryParametersForPagination,
  organizationCode,
}: UseFetchParameters): UseFetchResponse<
  DtwinOperation<Record<string, string>>
> {
  const service = OperationManagerService.getOperations.bind(
    OperationManagerService,
  );

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.OPERATION_CAMPAIGN_UID,
      {
        key: "campaignUid",
        operator: OPERATOR_RSQL_IN,
        value: [],
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.DTWIN_UID,
      {
        key: "deviceTwinUid",
        operator: OPERATOR_RSQL_IN,
        value: "",
      },
    ],
    [
      Filters.DTWIN_OPERATION_MODEL_TYPE,
      {
        key: "type",
        operator: OPERATOR_RSQL_IN,
        value: "",
      },
    ],
    [
      Filters.DTWIN_CREATED_IN_CAMPAIGN,
      {
        key: "createdInCampaign",
        operator: OPERATOR_RSQL_EQUAL,
        value: "",
      },
    ],
    [
      Filters.DTWINS_CONFIGURATION_VERSIONS,
      {
        key: [
          "customInputParameters.selfSupportingVersion",
          "customInputParameters.globalVersion",
          "specificVersion",
        ],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.SECTORS,
      {
        key: "sectorCode",
        operator: OPERATOR_RSQL_IN,
        value: "",
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPage({
    service,
    searchParams,
    sort,
    useQueryParametersForPagination,
    organizationCode,
  });

  return {
    isLoading,
    error,
    results,
    filters,
    setFilter,
    fetchData,
    onSortChange,
    search,
    onSearch,
    sort,
  };
}
